const SOURCE_LANGUAGE = 'en';
const CACHE_LIMIT = 1000;
const TRANSLATE_TIMEOUT_MS = 15000;

/**
 * Whitelisted public catalogue categories. The official property name is
 * deliberately absent: it is a brand identifier and must always render in
 * its source form regardless of locale.
 */
export type DynamicTranslationCategory =
  | 'propertyDescription'
  | 'propertyAddress'
  | 'roomName'
  | 'roomDescription'
  | 'amenity'
  | 'bedType'
  | 'ratePlanName'
  | 'cancellationPolicy'
  | 'paymentPolicy'
  | 'promotion'
  | 'inclusion'
  | 'package'
  /** Geographic display names in the location picker (country/state/city). `hotel` node titles are official property names and never use this category. */
  | 'locationName'
  /** Descriptive customer-facing image alt text (e.g. reservation cover). Property-name alts never use this category. */
  | 'imageAlt';

const ALLOWED_CATEGORIES: ReadonlySet<string> = new Set<DynamicTranslationCategory>([
  'propertyDescription',
  'propertyAddress',
  'roomName',
  'roomDescription',
  'amenity',
  'bedType',
  'ratePlanName',
  'cancellationPolicy',
  'paymentPolicy',
  'promotion',
  'inclusion',
  'package',
  'locationName',
  'imageAlt',
]);

export type TranslatorAvailability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

export interface BrowserTranslator {
  translate(input: string): Promise<string>;
}

interface DownloadMonitorTarget {
  addEventListener(
    type: 'downloadprogress',
    listener: (event: { readonly loaded: number }) => void,
  ): void;
}

interface BrowserTranslatorConstructor {
  availability(options: {
    sourceLanguage: string;
    targetLanguage: string;
  }): Promise<TranslatorAvailability>;
  create(options: {
    sourceLanguage: string;
    targetLanguage: string;
    monitor?: (monitor: DownloadMonitorTarget) => void;
  }): Promise<BrowserTranslator>;
}

function getTranslatorConstructor(): BrowserTranslatorConstructor | undefined {
  const scope = globalThis as { Translator?: BrowserTranslatorConstructor };
  return scope.Translator;
}

export function isTranslatorApiSupported(): boolean {
  return getTranslatorConstructor() !== undefined;
}

const availabilityByTarget = new Map<string, Promise<TranslatorAvailability>>();
const translatorByTarget = new Map<string, Promise<BrowserTranslator | null>>();
const translationCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

export function checkAvailability(targetLanguage: string): Promise<TranslatorAvailability> {
  const ctor = getTranslatorConstructor();
  if (ctor === undefined) return Promise.resolve('unavailable');
  const cached = availabilityByTarget.get(targetLanguage);
  if (cached !== undefined) return cached;
  const pending = ctor
    .availability({ sourceLanguage: SOURCE_LANGUAGE, targetLanguage })
    .catch(() => 'unavailable' as const);
  availabilityByTarget.set(targetLanguage, pending);
  return pending;
}

export function getOrCreateTranslator(targetLanguage: string): Promise<BrowserTranslator | null> {
  const existing = translatorByTarget.get(targetLanguage);
  if (existing !== undefined) return existing;
  const created = (async (): Promise<BrowserTranslator | null> => {
    const ctor = getTranslatorConstructor();
    if (ctor === undefined) return null;
    const availability = await checkAvailability(targetLanguage);
    if (availability === 'unavailable') return null;
    try {
      return await ctor.create({
        sourceLanguage: SOURCE_LANGUAGE,
        targetLanguage,
      });
    } catch {
      return null;
    }
  })();
  translatorByTarget.set(targetLanguage, created);
  return created;
}

/**
 * Re-attempts translator creation for a target whose earlier `create()`
 * failed (e.g. `NotAllowedError` on refresh). Call only from a user-activated
 * path such as the language dropdown selection.
 */
export function retryTranslator(targetLanguage: string): Promise<BrowserTranslator | null> {
  translatorByTarget.delete(targetLanguage);
  return getOrCreateTranslator(targetLanguage);
}

function cacheSet(key: string, value: string): void {
  if (translationCache.size >= CACHE_LIMIT) {
    const oldest = translationCache.keys().next();
    if (!oldest.done) translationCache.delete(oldest.value);
  }
  translationCache.set(key, value);
}

function withTimeout(promise: Promise<string>, fallback: string): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<string>((resolve) => {
    timer = setTimeout(() => {
      resolve(fallback);
    }, TRANSLATE_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/**
 * Translates one whitelisted public catalogue string en → `targetLanguage`
 * via the browser's Translator API. Every failure path — no API, unavailable
 * pair, create/translate errors, timeouts, empty results — resolves to the
 * original `text`. Results are cached and concurrent calls deduplicated.
 */
export async function translateContent(
  category: DynamicTranslationCategory,
  targetLanguage: string,
  text: string | null | undefined,
): Promise<string> {
  const source = text ?? '';
  if (source === '' || targetLanguage === SOURCE_LANGUAGE) return source;
  if (!ALLOWED_CATEGORIES.has(category)) return source;
  const key = `${SOURCE_LANGUAGE}|${targetLanguage}|${source}`;
  const cached = translationCache.get(key);
  if (cached !== undefined) return cached;
  const pending = inFlight.get(key);
  if (pending !== undefined) return await pending;
  const request = (async (): Promise<string> => {
    const translator = await getOrCreateTranslator(targetLanguage);
    if (translator === null) return source;
    const translated = await withTimeout(translator.translate(source), source);
    return translated !== '' ? translated : source;
  })();
  inFlight.set(key, request);
  try {
    const result = await request;
    if (result !== source) cacheSet(key, result);
    return result;
  } catch {
    return source;
  } finally {
    inFlight.delete(key);
  }
}

/** Clears all caches — test-only helper. */
export function resetContentTranslator(): void {
  availabilityByTarget.clear();
  translatorByTarget.clear();
  translationCache.clear();
  inFlight.clear();
}
